import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { RichText } from "@/components/site/RichText";
import { useCallback, useEffect, useState } from "react";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CinematicHero } from "@/components/site/CinematicHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ProjectGrid } from "@/components/site/ProjectGrid";
import { CtaSplit } from "@/components/site/CtaSplit";
import { ChevronLeft, ChevronRight, X, Check } from "lucide-react";
import { mediaQuery } from "@/lib/media-queries";
import { opbouwPhases } from "@/content/opbouw-phases";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { canonical, breadcrumbJsonLd, shareImageMeta, langPageMeta } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/opbouw-zwembad";
const SLEUTEL = "opbouw-zwembad";

const CODE_META_TITLE = "Zwembad bouwen stap voor stap in foto's | A tot Z";
const CODE_META_DESCRIPTION =
  "Volg de volledige opbouw van een bouwkundig zwembad in foto's: isolatie met 3 cm Recticel, polyester lamineren, schuren, topcoat, technieken, rolluik en oplevering.";

export const Route = createFileRoute("/$lang/opbouw-zwembad")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => {
    const [assets, page] = await Promise.all([
      context.queryClient.ensureQueryData(mediaQuery("opbouw")),
      context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
    ]);
    const firstPhoto = opbouwPhases.flatMap((phase) => assets.filter((asset) => asset.phase === phase.short))[0];
    return { page, heroFallback: { src: firstPhoto?.url ?? "", variants: firstPhoto?.variants } };
  },
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), loaderData?.heroFallback ?? { src: "" });
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: "Van ruwbouw tot oplevering: elke fase van een bouwkundig zwembad in beeld." },
        { property: "og:type", content: "article" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("opbouw-zwembad", "Afgewerkt bouwkundig zwembad na de volledige opbouw van A tot Z"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [faqJsonLd(faqs.opbouw), breadcrumbJsonLd("Opbouw van A tot Z", PATH)],
    };
  },
  component: OpbouwPage,
});

type Photo = { src: string; variants?: import("@/lib/image-delivery").DeliveryVariant[]; width: number; height: number; alt: string };

const roadmap = [
  "Grondwerken",
  "Ruwbouw kuip",
  "Isolatie",
  "Polyester",
  "Schuren",
  "Topcoat",
  "Verlichting",
  "Technieken",
  "Afdekking",
  "Oplevering",
];

const donePhases = new Set([
  "Isolatie",
  "Polyester",
  "Schuren",
  "Topcoat",
  "Verlichting",
  "Open lucht",
  "Technieken",
  "Afdekking",
  "Oplevering",
]);

function OpbouwPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const [index, setIndex] = useState<number | null>(null);
  const { data: assets } = useSuspenseQuery(mediaQuery("opbouw"));

  const phases = opbouwPhases.map((phase) => ({
    ...phase,
    photos: assets
      .filter((asset) => asset.phase === phase.short)
      .map<Photo>((asset) => ({
        src: asset.url,
        variants: asset.variants,
        width: asset.width ?? 1200,
        height: asset.height ?? 1600,
        alt: asset.alt,
      })),
  }));
  const allPhotos = phases.flatMap((phase) => phase.photos);
  const hero = c.section("hero");
  const heroImage = hero?.mediaUrl ?? allPhotos[0]?.src ?? "";
  const heroPhoto = heroDelivery(hero, { src: heroImage, variants: allPhotos[0]?.variants });
  const heroAlt = c.veld("hero", "alt", "Bouwkundig zwembad tijdens de opbouw van A tot Z");

  const close = useCallback(() => setIndex(null), []);
  const total = allPhotos.length;
  const step = useCallback(
    (dir: number) =>
      setIndex((current) => (current === null || total === 0 ? null : (current + dir + total) % total)),
    [total],
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, close, step]);

  const active = index === null ? null : allPhotos[index];

  return (
    <div className="flex flex-col">
      <CinematicHero
        overline={c.veld("hero", "overline", "Projectverslag")}
        title={c.veld("hero", "titel", "Een zwembad van A tot Z: zo bouwen wij")}
        subtitle={c.rijkInline("hero",
          "Volg dit project stap voor stap mee, van de ruwe kuip tot de definitieve oplevering. Elke fase krijgt hier zijn eigen beelden, zodat u precies ziet wat er achter een afgewerkt zwembad schuilgaat.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={heroAlt}
      />

      <Section tone="light">
        <div className="grid gap-10 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16">
          <div>
            <Overline>{c.veld("werkwijze", "overline", "Werkwijze")}</Overline>
            <h2 className="mt-4 display-section">
              {c.veld("werkwijze", "titel", "Hoe wordt een bouwkundig zwembad gebouwd?")}
            </h2>
          </div>
          <div className="border-l border-border pl-6 lg:pl-12">
            {c
              .alineas("werkwijze", [
                "Een bouwkundig zwembad ontstaat in een vaste volgorde: grondwerken en betonkuip, isolatie met 3 cm Recticel op vloer en wanden, lamineren van de polyesterbekleding, schuren, een topcoat in de gekozen kleur, plaatsen en testen van verlichting en technieken, en tot slot boordstenen, afdekking en oplevering.",
                "Op deze pagina ziet u dat volledige traject van één echt project in Limburg, fase per fase en met echte werffoto's — geen stockbeelden. Zo weet u vooraf hoe zo'n bouw er in de praktijk uitziet, hoeveel werk er in de onzichtbare lagen kruipt en waarom wij onder een werftent werken.",
              ])
              .map((alinea, index2) => (
                <p
                  key={alinea.slice(0, 24)}
                  className={`${index2 === 0 ? "" : "mt-4"} text-[17px] leading-7 text-on-surface-variant`}
                >
                  <RichText waarde={alinea} inline />
                </p>
              ))}
          </div>
        </div>
      </Section>

      {/* Voortgang */}
      <Section tone="navy" compact>
        <Overline onDark>{c.veld("voortgang", "overline", "Voortgang")}</Overline>
        <ol className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-3">
          {roadmap.map((label, i) => {
            const done = donePhases.has(label);
            return (
              <li key={label} className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium ${
                    done
                      ? "bg-secondary text-secondary-foreground"
                      : "border border-primary-foreground/25 text-primary-foreground/80"
                  }`}
                >
                  {done ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={1.75} />
                  ) : (
                    <span className="text-xs tabular-nums">{i + 1}</span>
                  )}
                  {label}
                </span>
                {i < roadmap.length - 1 && (
                  <span className="hidden h-px w-6 bg-primary-foreground/25 sm:block" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
        <p className="mt-6 text-sm text-primary-foreground/80">
          {c.rijkInline("voortgang", "Het volledige verslag: van isolatie tot oplevering, fase per fase in beeld.")}
        </p>
      </Section>

      {/* Fasen */}
      <div>
        {phases.map((phase, phaseIndex) => {
          const offset = phases.slice(0, phaseIndex).reduce((total2, p) => total2 + p.photos.length, 0);

          return (
            <Section key={phase.id} tone={phaseIndex % 2 === 0 ? "light" : "mist"}>
              <article className="grid gap-8 lg:grid-cols-[0.35fr_1.65fr] lg:gap-16">
                <div>
                  <span className="font-display text-sm font-semibold text-brand-deep">
                    {String(phaseIndex + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-4 font-display text-[clamp(1.75rem,3vw,2.5rem)] font-bold leading-[1.1]">
                    {phase.title}
                  </h2>
                  <p className="mt-4 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">{phase.description}</p>
                </div>
                <ProjectGrid
                  className="lg:grid-cols-2 xl:grid-cols-3"
                  items={phase.photos.map((photo, i) => ({
                    src: photo.src,
                    variants: photo.variants ?? [],
                    alt: photo.alt,
                    onSelect: () => setIndex(offset + i),
                  }))}
                />
              </article>
            </Section>
          );
        })}

        <Section tone="light">
          <article className="border-l-2 border-brand-deep-water pl-6 lg:pl-10">
            <Check className="h-5 w-5 text-brand-deep" strokeWidth={1.75} />
            <h2 className="font-display text-[clamp(1.5rem,3vw,1.875rem)] font-semibold leading-[1.2]">
              {c.veld("opgeleverd", "titel", "Project opgeleverd")}
            </h2>
            <p className="mt-4 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("opgeleverd",
                "Van de eerste isolatieplaat tot het gevulde bad: dit project is volledig afgerond. Bekijk meer afgewerkte zwembaden op onze realisatiepagina, of start uw eigen traject met een vrijblijvende offerte.",
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild>
                <LocaleLink to={c.veld("opgeleverd", "knopLink", "/realisaties") as PublicPath}>
                  {c.veld("opgeleverd", "knopTekst", "Bekijk realisaties")}
                </LocaleLink>
              </Button>
              <Button asChild variant="outline">
                <LocaleLink to={c.veld("opgeleverd", "knop2Link", "/offerte") as PublicPath}>
                  {c.veld("opgeleverd", "knop2Tekst", "Offerte aanvragen")}
                </LocaleLink>
              </Button>
            </div>
          </article>
        </Section>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={active.alt}
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/90 p-4"
          onClick={close}
        >
          <button
            type="button"
            aria-label="Sluiten"
            onClick={close}
            className="absolute right-4 top-4 rounded-full bg-background/90 p-2 text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Vorige foto"
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
            className="absolute left-4 rounded-full bg-background/90 p-2 text-foreground"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Volgende foto"
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
            className="absolute right-4 top-1/2 rounded-full bg-background/90 p-2 text-foreground"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <figure className="flex max-h-full flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={active.src}
              alt={active.alt}
              width={active.width}
              height={active.height}
              className="max-h-[80vh] w-auto max-w-full object-contain"
            />
            <figcaption className="max-w-xl text-center text-sm text-background">{active.alt}</figcaption>
          </figure>
        </div>
      )}

      <CtaSplit
        overline={c.veld("cta", "overline", "Uw project")}
        title={c.veld("cta", "titel", "Zin in uw eigen project?")}
        text={c.rijkInline("cta", "Wij begeleiden u van de eerste schop in de grond tot de laatste boordsteen.")}
        imageSrc={heroImage}
        imageAlt={heroAlt}
        ctaLabel={c.veld("cta", "knopTekst", "Offerte aanvragen")}
      />
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.opbouw} />
    </div>
  );
}
